import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import './RefundPage.css';
import useFreshIdToken from '../auth/useFreshIdToken';
import { WA_CS_LINK } from '../config/whatsapp';
import useSimRefund from './useSimRefund';
import { INELIGIBLE_LABELS, REFUND_REASONS, createSimRefund } from './refundApi';
import { useLanguage } from '../i18n/LanguageContext';

const NOTE_MAX = 500;

const RefundRequestPage = () => {
    const { SimId } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
    const getToken = useFreshIdToken();
    const refundState = useSimRefund(SimId);
    const { t } = useLanguage();

    const [sim, setSim] = useState(null);
    const [reason, setReason] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    // A dictionary key, so the message follows the current language.
    const [submitError, setSubmitError] = useState('');

    // Same pattern as checkout: an anonymous visitor is sent to log in and
    // brought back here by AuthCallback.
    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            localStorage.setItem('redirectAfterLogin', pathname);
            loginWithRedirect().catch((e) => console.error('Login error:', e));
        }
    }, [isLoading, isAuthenticated, loginWithRedirect, pathname]);

    useEffect(() => {
        fetch(`https://api.artamobile.id/v1/esims/id/${SimId}`)
            .then((res) => (res.ok ? res.json() : null))
            .then(setSim)
            .catch(() => setSim(null));
    }, [SimId]);

    if (refundState.refund) {
        return <Navigate to={`/sim/${SimId}/refund/status`} replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        if (!reason || submitting) return;

        setSubmitting(true);
        setSubmitError('');
        try {
            const token = await getToken();
            await createSimRefund(SimId, token, { reason, note: note.trim() });
            navigate(`/sim/${SimId}/refund/status`, { replace: true });
        } catch (error) {
            if (error.status === 409) {
                navigate(`/sim/${SimId}/refund/status`, { replace: true });
                return;
            }
            if (error.status === 422) {
                setSubmitError(INELIGIBLE_LABELS[error.ineligibleReason] || 'refund.request.notEligibleDefault');
            } else {
                setSubmitError(error.message === 'Failed to fetch'
                    ? 'refund.request.networkError'
                    : 'refund.request.submitError');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const esim = sim?.esim;
    const region = sim?.region;
    const plan = sim?.plans?.[0];
    const title = esim?.label || (region ? `eSIM ${region.name}` : 'eSIM');
    const eligibility = refundState.eligibility;

    let body;
    if (refundState.status === 'loading' || refundState.status === 'anonymous') {
        body = <div className="rf-state">{t('refund.request.checking')}</div>;
    } else if (refundState.status === 'forbidden') {
        body = (
            <div className="rf-notice danger">
                <AlertCircle size={22} className="rf-notice-icon" />
                <div>
                    <strong>{t('refund.request.forbiddenTitle')}</strong>
                    <p>{t('refund.request.forbiddenBody')}</p>
                </div>
            </div>
        );
    } else if (refundState.status === 'error') {
        body = (
            <div className="rf-notice danger">
                <AlertCircle size={22} className="rf-notice-icon" />
                <div>
                    <strong>{t('refund.request.errorTitle')}</strong>
                    <p>{t('refund.request.errorBody')}</p>
                    <button type="button" className="rf-link-btn" onClick={refundState.reload}>{t('refund.request.retry')}</button>
                </div>
            </div>
        );
    } else if (!eligibility?.eligible) {
        body = (
            <div className="rf-notice danger">
                <AlertCircle size={22} className="rf-notice-icon" />
                <div>
                    <strong>{t('refund.request.notEligibleTitle')}</strong>
                    <p>{t(INELIGIBLE_LABELS[eligibility?.reason] || 'refund.request.notEligibleDefault')} {t('refund.request.contactCs')}</p>
                    <a className="rf-link-btn" href={WA_CS_LINK} target="_blank" rel="noopener noreferrer">{t('refund.request.chatCs')}</a>
                </div>
            </div>
        );
    } else {
        body = (
            <form id="refund-form" className="rf-stack" onSubmit={submit}>
                <div className="rf-notice success">
                    <CheckCircle2 size={22} className="rf-notice-icon" />
                    <div>
                        <strong>{t('refund.request.eligibleTitle')}</strong>
                        <p>{t('refund.request.eligibleBody', { sla: t('refund.slaLabel') })}</p>
                    </div>
                </div>

                <fieldset className="rf-fieldset">
                    <legend>{t('refund.request.reasonLegend')}</legend>
                    {REFUND_REASONS.map((value) => (
                        <label key={value} className={`rf-radio ${reason === value ? 'checked' : ''}`}>
                            <input
                                type="radio"
                                name="reason"
                                value={value}
                                checked={reason === value}
                                onChange={() => setReason(value)}
                            />
                            {t(`refund.reasons.${value}`)}
                        </label>
                    ))}
                </fieldset>

                <div className="rf-field">
                    <label htmlFor="rf-note">{t('refund.request.note')} <span>{t('refund.request.optional')}</span></label>
                    <textarea
                        id="rf-note"
                        rows={3}
                        maxLength={NOTE_MAX}
                        placeholder={t('refund.request.notePlaceholder')}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </div>

                {submitError && <p className="rf-error" role="alert">{t(submitError)}</p>}
            </form>
        );
    }

    const canSubmit = refundState.status === 'ready' && eligibility?.eligible;

    return (
        <div className="rf-page">
            <header className="rf-header">
                <button className="rf-icon-btn" onClick={() => navigate(`/sim/${SimId}`)} aria-label={t('common.back')}>
                    <ChevronLeft size={22} />
                </button>
                <h1>{t('refund.request.title')}</h1>
            </header>

            <main className="rf-body">
                <div className="rf-card">
                    <div className="rf-card-row">
                        <strong>{title}</strong>
                    </div>
                    {esim?.iccid && <p className="rf-muted">ICCID: {esim.iccid}</p>}
                    {plan?.validity != null && <p className="rf-muted">{t('refund.request.validity', { n: plan.validity })}</p>}
                </div>
                {body}
            </main>

            {canSubmit && (
                <footer className="rf-footer">
                    <p className="rf-muted">{t('refund.request.footerNote')}</p>
                    <button type="submit" form="refund-form" className="rf-primary" disabled={!reason || submitting}>
                        {submitting ? t('refund.request.sending') : t('refund.request.title')}
                    </button>
                </footer>
            )}
        </div>
    );
};

export default RefundRequestPage;
